import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, Table } from "reactstrap";
import automationLogService from "../../../../services/automation_log_service";
import { useTenant } from "../../../../hooks/useTenant";
import { ContentLoader, NeoBadge } from "../../../../components/Common";

/**
 * Mapeia status → classe CSS semântica + ícone para exibição visual.
 */
const STATUS_MAP = {
    QUEUED: { label: "Na fila", color: "muted", icon: "mdi-clock-outline" },
    SENT: { label: "Enviado", color: "chocolate", icon: "mdi-send-check" },
    DELIVERED: { label: "Entregue", color: "accent", icon: "mdi-check-all" },
    READ: { label: "Lido", color: "success", icon: "mdi-eye-check-outline" },
    FAILED: { label: "Falhou", color: "danger", icon: "mdi-alert-circle-outline" },
};

/**
 * Formata uma data para exibição amigável.
 */
const formatDateTime = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
    });
};

/**
 * AutomationLogModal
 * Modal que exibe o histórico de envios de um gatilho de automação.
 */
const AutomationLogModal = ({ isOpen, toggle, triggerId, triggerLabel }) => {
    const { idTenant } = useTenant();
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedLogId, setExpandedLogId] = useState(null);

    const loadLogs = useCallback(async () => {
        if (!idTenant || !triggerId) return;
        setLoading(true);
        try {
            const [logsData, statsData] = await Promise.all([
                automationLogService.getLogsByTrigger(idTenant, triggerId, 100),
                automationLogService.getStatsByTrigger(idTenant, triggerId),
            ]);
            setLogs(logsData);
            setStats(statsData);
        } catch (err) {
            console.error("Erro ao carregar logs:", err);
        } finally {
            setLoading(false);
        }
    }, [idTenant, triggerId]);

    useEffect(() => {
        if (isOpen) {
            loadLogs();
            setExpandedLogId(null);
        }
    }, [isOpen, loadLogs]);

    const toggleExpand = (logId) => {
        setExpandedLogId(prev => prev === logId ? null : logId);
    };

    return (
        <Modal 
            isOpen={isOpen} 
            toggle={toggle} 
            size="lg" 
            centered 
            scrollable
            className="neo-font-outfit"
            contentClassName="premium-modal border-0"
        >
            <ModalHeader toggle={toggle} className="border-bottom py-3 px-4">
                <div className="d-flex align-items-center">
                    <div className="neo-section-icon-circle flex-shrink-0 me-3">
                        <i className="mdi mdi-history font-size-18 text-accent"></i>
                    </div>
                    <div>
                        <span className="fw-bold text-dark font-size-15 d-block">Histórico de Envios</span>
                        <small className="text-muted font-size-11">{triggerLabel}</small>
                    </div>
                </div>
            </ModalHeader>
            <ModalBody className="p-0 bg-white">
                {/* Cards de estatísticas */}
                {stats && (
                    <div className="d-flex gap-3 p-4 border-bottom bg-light bg-opacity-30 flex-wrap">
                        <StatCard label="Total" value={stats.total} icon="mdi-email-outline" color="#784b22" />
                        <StatCard label="Enviados" value={stats.sent} icon="mdi-send-check" color="#a05a2c" />
                        <StatCard label="Entregues" value={stats.delivered} icon="mdi-check-all" color="#e2661c" />
                        <StatCard label="Lidos" value={stats.read} icon="mdi-eye-check-outline" color="#2e7d32" />
                        <StatCard label="Falhas" value={stats.failed} icon="mdi-alert-circle-outline" color="#c62828" />
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="py-4">
                        <ContentLoader message="Carregando histórico..." />
                    </div>
                )}

                {/* Sem logs */}
                {!loading && logs.length === 0 && (
                    <div className="text-center py-5 px-3">
                        <i className="mdi mdi-email-off-outline font-size-48 text-muted opacity-25 d-block mb-3"></i>
                        <p className="text-muted fw-bold mb-1 font-size-13">Nenhum envio registrado</p>
                        <small className="text-muted d-block font-size-11" style={{ maxWidth: "320px", margin: "0 auto" }}>
                            Os logs de envio aparecerão assim que a automação disparar as primeiras mensagens.
                        </small>
                    </div>
                )}

                {/* Tabela de logs */}
                {!loading && logs.length > 0 && (
                    <div style={{ maxHeight: '420px', overflowY: 'auto' }} className="custom-scrollbar">
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th className="font-size-11 fw-bold text-muted py-2.5 ps-4 text-uppercase">Destinatário</th>
                                    <th className="font-size-11 fw-bold text-muted py-2.5 text-uppercase">Canal</th>
                                    <th className="font-size-11 fw-bold text-muted py-2.5 text-uppercase">Status</th>
                                    <th className="font-size-11 fw-bold text-muted py-2.5 text-uppercase">Data</th>
                                    <th className="font-size-11 fw-bold text-muted py-2.5 pe-4" style={{ width: '40px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <React.Fragment key={log.id}>
                                        <tr
                                            onClick={() => toggleExpand(log.id)}
                                            style={{ cursor: 'pointer' }}
                                            className={expandedLogId === log.id ? 'table-active' : ''}
                                        >
                                            <td className="ps-4 py-3">
                                                <div>
                                                    <span className="fw-bold font-size-13 text-dark d-block">{log.recipientName}</span>
                                                    <small className="text-muted font-size-11 d-flex align-items-center">
                                                        {log.recipientType === "employee" ? (
                                                            <><i className="mdi mdi-teach me-1 font-size-13" style={{ color: "#784b22" }}></i>Professor</>
                                                        ) : (
                                                            <><i className="mdi mdi-account-outline me-1 font-size-13" style={{ color: "#e2661c" }}></i>Aluno</>
                                                        )}
                                                    </small>
                                                </div>
                                            </td>
                                            <td className="py-3">
                                                <ChannelBadge channel={log.channel} />
                                            </td>
                                            <td className="py-3">
                                                <StatusBadge status={log.status} />
                                            </td>
                                            <td className="py-3">
                                                <small className="text-muted fw-semibold font-size-11">{formatDateTime(log.createdAt)}</small>
                                            </td>
                                            <td className="pe-4 py-3 text-end">
                                                <i className={`mdi ${expandedLogId === log.id ? 'mdi-chevron-up' : 'mdi-chevron-down'} font-size-18`} style={{ color: expandedLogId === log.id ? '#e2661c' : '#8e8e93' }}></i>
                                            </td>
                                        </tr>

                                        {/* Detalhes expandidos */}
                                        {expandedLogId === log.id && (
                                            <tr>
                                                <td colSpan="5" className="p-0">
                                                    <div className="bg-light bg-opacity-30 p-4 border-top border-bottom">
                                                        <div className="row g-3">
                                                            {/* Mensagem */}
                                                            <div className="col-12">
                                                                <label className="fw-bold font-size-11 text-muted text-uppercase mb-2 d-block">
                                                                    Mensagem Enviada
                                                                </label>
                                                                <div
                                                                    className="bg-white p-3 font-size-12"
                                                                    style={{ 
                                                                        whiteSpace: 'pre-wrap', 
                                                                        maxHeight: '160px', 
                                                                        overflowY: 'auto',
                                                                        borderRadius: "10px",
                                                                        border: "1.5px solid rgba(72, 39, 18, 0.10)",
                                                                        color: "#482712",
                                                                        fontFamily: "var(--bs-font-sans-serif)"
                                                                    }}
                                                                >
                                                                    {log.messageFull || log.messagePreview || "—"}
                                                                </div>
                                                            </div>

                                                            {/* Detalhes do contato */}
                                                            <div className="col-md-6">
                                                                <label className="fw-bold font-size-11 text-muted text-uppercase mb-2 d-block">
                                                                    Contato
                                                                </label>
                                                                <div className="font-size-12 d-flex flex-column gap-1">
                                                                    {log.recipientPhone && (
                                                                        <div className="d-flex align-items-center"><i className="mdi mdi-phone me-2 text-muted font-size-14"></i>{log.recipientPhone}</div>
                                                                    )}
                                                                    {log.recipientEmail && (
                                                                        <div className="d-flex align-items-center"><i className="mdi mdi-email-outline me-2 text-muted font-size-14"></i>{log.recipientEmail}</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Timeline de status */}
                                                            <div className="col-md-6">
                                                                <label className="fw-bold font-size-11 text-muted text-uppercase mb-2 d-block">
                                                                    Linha do Tempo
                                                                </label>
                                                                <div className="font-size-11 d-flex flex-column gap-1 fw-medium">
                                                                    {log.sentAt && (
                                                                        <div className="d-flex align-items-center" style={{ color: "#784b22" }}><i className="mdi mdi-send me-2 font-size-13"></i>Disparado: {formatDateTime(log.sentAt)}</div>
                                                                    )}
                                                                    {log.deliveredAt && (
                                                                        <div className="d-flex align-items-center" style={{ color: "#e2661c" }}><i className="mdi mdi-check-all me-2 font-size-13"></i>Entregue: {formatDateTime(log.deliveredAt)}</div>
                                                                    )}
                                                                    {log.readAt && (
                                                                        <div className="text-success d-flex align-items-center"><i className="mdi mdi-eye-check me-2 font-size-13"></i>Lido: {formatDateTime(log.readAt)}</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Erro (se houver) */}
                                                            {log.status === "FAILED" && log.errorMessage && (
                                                                <div className="col-12 mt-3">
                                                                    <div className="alert alert-danger mb-0 py-2.5 px-3 font-size-12" style={{ borderRadius: "8px" }}>
                                                                        <i className="mdi mdi-alert-circle me-2 font-size-14 align-middle"></i>
                                                                        <strong>Erro ({log.errorCode}): </strong>{log.errorMessage}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

/**
 * Mini card de estatística.
 */
const StatCard = ({ label, value, icon, color }) => (
    <div
        className="d-flex align-items-center gap-3 px-3 py-2 flex-fill"
        style={{ 
            backgroundColor: color + '12', 
            minWidth: '110px',
            borderRadius: "10px",
            border: `1px solid ${color}20`
        }}
    >
        <div 
            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: "32px", height: "32px", backgroundColor: color + "18" }}
        >
            <i className={`mdi ${icon} font-size-16`} style={{ color }}></i>
        </div>
        <div>
            <div className="fw-bold font-size-16" style={{ color, lineHeight: 1.1 }}>{value}</div>
            <div className="font-size-10 text-uppercase fw-bold mt-0.5" style={{ letterSpacing: "0.2px", color: "rgba(72, 39, 18, 0.60)" }}>{label}</div>
        </div>
    </div>
);

/**
 * Badge de canal (WhatsApp, Email, SMS, Push).
 */
const ChannelBadge = ({ channel }) => {
    const map = {
        whatsapp: { icon: "mdi-whatsapp", color: "#25D366", label: "WhatsApp" },
        email: { icon: "mdi-email-outline", color: "#784b22", label: "E-mail" },
        sms: { icon: "mdi-message-text-outline", color: "#fd7e14", label: "SMS" },
        push: { icon: "mdi-bell-outline", color: "#e2661c", label: "Push" },
    };
    const info = map[channel] || map.whatsapp;
    return (
        <span className="d-inline-flex align-items-center gap-1.5 font-size-12 fw-semibold text-dark">
            <i className={`mdi ${info.icon} font-size-14`} style={{ color: info.color }}></i>
            {info.label}
        </span>
    );
};

/**
 * Badge de status do envio.
 */
const StatusBadge = ({ status }) => {
    const info = STATUS_MAP[status] || STATUS_MAP.QUEUED;
    return (
        <NeoBadge color={info.color} icon={info.icon}>
            {info.label}
        </NeoBadge>
    );
};

export default AutomationLogModal;
